import { mapState, mapActions, mapMutations } from 'vuex'
import { apiGetDamage } from '@/modules/Damage/services/damages'
import userLoginMixin from '@/modules/User/mixins/userLoginMixin'
import userFlowMixin from '@/modules/User/mixins/userFlowMixin'
import flowMixin from '@/modules/Flow/mixins/flowMixin'
import maNotifications from '@/components/notifications'
import maLoader from '@/components/loader'
import { USER_MODULE_NAME } from '@/modules/User/store/types'
import {
  WARRANTY_MODULE_NAME,
  WARRANTY_GET_SINISTER,
  WARRANTY_GET_SINISTER_SUMMARY,
  WARRANTY_UPDATE_SINISTER_SUMMARY,
  FNOL_MODULE_NAME,
  FNOL_UPDATE_ID_FLUX
} from '@/store/types'
import maNavBar from './components/navBar'
import maButtonPhone from '@/components/buttonPhone'
/* TO REMOVE import maEventBus from '@/components/eventbus/index.js' */
import warrantyModule from '@/store/modules/warranty'
import billmaHttpClient from '@/services/http/billmaHttpClient'
import {
  setUserType,
  getUserType,
  isValidUserType,
  isPhotoclaim,
  isBillmaAgentNew,
  isE2ENew
} from '@/utils/userSettings'
import { exposeSurveyConfig } from '@/utils/inmomentSurvey'
import { prepareErrorForSentry } from '@/utils/errorHandling'
import { errorsTrack } from '@/utils/registeredErrors'
import { isE2E } from '../utils/userSettings'

export default {
  name: 'App',

  mixins: [userLoginMixin, userFlowMixin, flowMixin],

  components: {
    maLoader,
    maNotifications,
    maNavBar,
    maButtonPhone
  },

  data: function () {
    return {
      appConfigIsLoaded: false,
      hasUserInteracted: false,
      activeComponent: null,
      deviceIsDesktop: false,
      renderDesktopMode: false,
      isBPCE: false
    }
  },

  computed: {
    ...mapState({
      stateLoader: (state) => state.loader,
      agentView: (state) => state.agentView
    }),
    ...mapState(USER_MODULE_NAME, {
      stateUserRoutes: 'routes'
    }),
    ...mapState(FNOL_MODULE_NAME, {
      stateIdFlux: 'idFlux'
    }),

    getFlowCompany() {
      return this.stateFlowCompany
    }
  },

  methods: {
    ...mapMutations(FNOL_MODULE_NAME, {
      mutFnolIdFlux: FNOL_UPDATE_ID_FLUX
    }),
    ...mapMutations(WARRANTY_MODULE_NAME, {
      mutWarrantyUpdateSinisterSummary: WARRANTY_UPDATE_SINISTER_SUMMARY
    }),
    ...mapActions(WARRANTY_MODULE_NAME, {
      actWarrantyGetSinister: WARRANTY_GET_SINISTER,
      actWarrantyGetSinisterSummary: WARRANTY_GET_SINISTER_SUMMARY
    }),

    matchLocale: function (locale) {
      const locales = this.$i18n.availableLocales
      if (locales.length <= 0) return
      return locales.indexOf(locale) !== -1 ? locale : locales[0]
    },

    initI18n: function (lang) {
      if (typeof Storage !== 'undefined') {
        let current
        if (lang) {
          current = this.matchLocale(lang)
        } else if (this.$route.query.lang) {
          current = this.matchLocale(this.$route.query.lang)
        } else if (localStorage.getItem('locale')) {
          current = this.matchLocale(localStorage.getItem('locale'))
        } else {
          current = 'fr'
        }
        this.$i18n.locale = current
        localStorage.setItem('locale', current)
      }
    },
    createSinisterContextError (error, sinisterId, methodName) {
      const context = {
        component: 'App',
        method: 'checkSinisterData.getOtpStatus',
        route: this.$route.path
      }
      const tags = prepareErrorForSentry(error, context)
      this.sendSentryError(
        `Error getting OTP status: ${error.message || 'Unknown error'}`,
        error.response && error.response.status >= 500 ? 'error' : 'warning',
        tags,
        sinisterId,
        `${methodName}.getOtpStatus`
      )
    },
    async checkSinisterData() {
      const sinisterId = this.getSinisterId
      const token = sessionStorage.getItem('mavAuth')
      const formId = sessionStorage.getItem('formId')
      const methodName = this.checkSinisterData.name.split(' ')[1]

      if (!sinisterId) {
        this.sendSentryError('Sinisted ID not found', 'error', {}, null, methodName)
        this.goToErrorView(this.$route.query)
        return
      }

      // NOTE Check if it's passing here
      if (token) {
        if (!formId) {
          this.sendSentryError('Form ID not found', 'error', {}, sinisterId, methodName)
          this.goToErrorView(this.$route.query)
          return
        }

        try {
          await this.getUserData()

          this.$router.replace({
            name: 'claim-home',
            query: { ...this.$route.query }
          })
        } catch (error) {
          // Create error context tags
          const context = {
            component: 'App',
            method: 'checkSinisterData.getUserData',
            route: this.$route.path
          }
          const tags = prepareErrorForSentry(error, context, { willRedirect: true })
          const err = error.response && error.response.status >= 500 && error.response.status < 600 ? 'error' : 'warning'
          this.sendSentryError(error.message, err, tags, sinisterId, `${methodName}.getUserData`)
          this.goToErrorView(this.$route.query)
        }

        return
      }

      if (!sessionStorage.getItem('otpSent')) {
        try {
          await this.getOtpStatus()
        } catch (error) {
          // Create error context tags
          this.createSinisterContextError(error, sinisterId, methodName)
          // NOTE Should we return to error page after this?
        }
      }
    },

    async getSinisterDamages() {
      if (!this.getSinisterId) return
      try {
        const damages = await apiGetDamage(this.getSinisterId)
        this.mutWarrantyUpdateSinisterSummary(damages)
      } catch (error) {
        // Create error context tags
        const context = {
          component: 'App',
          method: 'getSinisterDamages',
          route: this.$route.path
        }
        const tags = prepareErrorForSentry(error, context)
        this.sendSentryError(
          `Error getting sinister damages: ${error.message || 'Unknown error'}`,
          error.response && error.response.status >= 500 ? 'error' : 'warning',
          tags,
          this.getSinisterId,
          'getSinisterDamages'
        )
      }
    },

    async handleFlowData() {
      if (!sessionStorage.getItem('mavAuth')) return
      try {
        const flowData = await this.getStateFlowData()
        const { flow, smartFluxId, lang, state, transitions, options } =
          flowData
        this.setFlowName(flow)
        this.setFlowState(state, transitions)
        if (options.content) this.setViewContent(options.content)
        this.setViewOptions(options)
        this.setFluxData(smartFluxId)
        this.initI18n(lang)

        return flowData
      } catch (error) {
        // Create error context tags
        const context = {
          component: 'App',
          method: 'handleFlowData',
          route: this.$route.path
        }
        const tags = prepareErrorForSentry(error, context)
        this.sendSentryError(
          `Error loading flow data: ${error.message || 'Unknown error'}`,
          error.response && error.response.status >= 500 ? 'error' : 'warning',
          tags,
          this.getSinisterId || sessionStorage.getItem('sinId'),
          'handleFlowData'
        )
        throw new Error(error)
      }
    },

    setFluxData(fluxId) {
      this.mutFnolIdFlux(fluxId)
    },

    checkScencrypt() {
      if (this.$route.name !== 'scencrypt') return

      const scencryptId = this.$route.params.scencryptId

      return billmaHttpClient.get(`/scencrypt/${scencryptId}`)
        .catch((error) => {
          // Create error context tags
          const context = {
            component: 'App',
            method: 'checkScencrypt',
            route: this.$route.path
          }
          const tags = prepareErrorForSentry(error, context)
          this.sendSentryError(
            `Error checking scencrypt: ${error.message || 'Unknown error'}`,
            error.response && error.response.status >= 500 ? 'error' : 'warning',
            tags,
            null,
            'checkScencrypt'
          )
          return Promise.reject(error)
        })
    },

    handleRedirectToAppEntrypoint() {
      if (this.$route.name !== 'otp') {
        this.$router.replace({ name: 'otp', query: { ...this.$route.query } })
      }
    },

    handleUserTypeOnInit() {
      // We check if there is defined in the url. If not, we check if it is defined by previous session in session storage.
      let userTypeQueryParam = this.$route.query.user_type

      if (!userTypeQueryParam || !isValidUserType(userTypeQueryParam)) {
        userTypeQueryParam = getUserType()
        this.$router.replace({ query: { user_type: userTypeQueryParam } })
      }

      setUserType(userTypeQueryParam)

      if (isPhotoclaim()) {
        this.setUserFlow('photoclaim')
      }

      if (isBillmaAgentNew()) {
        this.setUserFlow('damage_billma_agent_new')
      }

      if (isE2ENew()) {
        this.setUserFlow('e2e_new')
      }

      if (isE2E()) {
        this.setUserFlow('e2e')
      }
    },

    /* TO REMOVE emitScrollEvent() {
      maEventBus.$emit('onScroll')
    }, */

    loadInMomentScript() {
      // Expose survey config on window.selfcareSurvey for every flow (E2E and
      // MesDommages) so it can be tested regardless of whether the intercept
      // script is loaded in that flow.
      exposeSurveyConfig()

      // Only load InMoment survey script for non-E2E flows
      if (isE2E() || isE2ENew()) {
        return
      }

      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.id = 'inmoment-intercept'
      script.src = 'inmoment-script.js'
      script.async = true
      document.head.appendChild(script)
    },

    initApp() {
      this.initI18n()
      this.checkScencrypt()
      this.handleUserTypeOnInit()
      this.loadInMomentScript()
    },

    handleBeforeUnload(event) {
      let message = 'User closed the window'
      if (errorsTrack.length > 0) {
        message += ' after receiving errors'
        this.sendSentryMessage(this.$route.matched[0].name, message, errorsTrack)
      }
      // Ensure the message is displayed in the browser prompt
      event.preventDefault()
      event.returnValue = '' // Required for legacy browsers
    }
    /* TO REMOVE userClosesWindow() {
      window.addEventListener('beforeunload', () => {
        const data = {
          activeComponent: this.activeComponent,
          hasUserInteracted: this.hasUserInteracted
        }

        let message = this.hasUserInteracted
          ? `User closed the window having interacted in ${this.activeComponent}`
          : `User closed the window without having interacted in ${this.activeComponent}`
        this.sendSentryEvent(message, this.userClosesWindow.name, data)
      })
    }, */

    /* TO REMOVE registerUserInteractions() {
      const userEvents = ['click', 'keydown', 'scroll', 'touchstart']
      this.hasUserInteracted = false

      userEvents.forEach((event) => {
        window[`on${event}`] = () => {
          this.hasUserInteracted = true
          userEvents.forEach((event) => (window[`on${event}`] = null))
        }
      })
    } */
  },

  beforeCreate: function () {
    if (!(WARRANTY_MODULE_NAME in this.$store.state)) {
      this.$store.registerModule(WARRANTY_MODULE_NAME, warrantyModule)
    }
  },

  created: async function () {
    if (this.$route.name === 'privacyPolicy') {
      this.appConfigIsLoaded = true
    } else {
      // Here we register the close window/tab event listener for this view
      window.addEventListener('beforeunload', this.handleBeforeUnload)

      this.deviceIsDesktop = !/Mobile/i.test(navigator.userAgent)

      if (this.deviceIsDesktop) {
        this.renderDesktopMode = window.innerWidth >= 800
        window.addEventListener(
          'resize',
          () => (this.renderDesktopMode = window.innerWidth >= 800)
        )

        if (this.$route.query.user_type === 'billma_bpce') {
          this.isBPCE = true
        }
      }

      try {
        this.handleRedirectToAppEntrypoint()
        this.initApp()
        await this.checkSinisterData()

        this.appConfigIsLoaded = true
      } catch (error) {
        // Create error context tags
        const context = {
          component: 'App',
          method: 'created',
          route: this.$route.path
        }
        const tags = prepareErrorForSentry(error, context, { willRedirect: true })
        this.sendSentryError(
          `Error during app initialization: ${error.message || 'Unknown error'}`,
          error.response && error.response.status >= 500 ? 'error' : 'warning',
          tags,
          this.getSinisterId || sessionStorage.getItem('sinId'),
          'created'
        )
        this.goToErrorView(this.$route.query)
        this.appConfigIsLoaded = true
      }

      // event bus for scrolling. This will be hear by components like selects
      /* TO REMOVE window.addEventListener('scroll', this.emitScrollEvent) */

      // TO REMOVE this.registerUserInteractions()
      // TO REMOVE this.userClosesWindow()

      /* TO REMOVE maEventBus.$on(
        'setActiveComponent',
        (component) => (this.activeComponent = component.$options.name)
      )
      maEventBus.$on('reinitializeUserInteraction', this.registerUserInteractions) */
    }
  },

  beforeDestroy: function () {
    // TO REMOVE window.removeEventListener('scroll', this.emitScrollEvent)
    // Here we register the close window/tab event listener for this view
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
    this.resetFlowState()
    sessionStorage.clear()
  }
}
